import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, GripVertical, Loader2, MessageCircle, Plus, Truck, X } from 'lucide-react';
import { API } from '../../../lib/api';
import { formatErr, notifyErr } from '../billing/shared';
import { aptWaUrl } from '../../../lib/aptMessages';

const iso = (d) => d.toLocaleDateString('en-CA');
const mondayOf = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); return x; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const ACTIVE = ['pending', 'confirmed'];
// Touch screens tap to open instead of dragging.
const FINE = typeof window !== 'undefined' && !window.matchMedia('(pointer: coarse)').matches;
const CHIP = {
  pending: 'border-amber-300 bg-amber-50 text-amber-900',
  confirmed: 'border-sky-300 bg-sky-50 text-sky-900',
  completed: 'border-emerald-300 bg-emerald-50 text-emerald-900',
  closed: 'border-slate-400 bg-slate-100 text-slate-700',
};

const Chip = ({ r, onOpen, onDrag }) => {
  const movable = FINE && ACTIVE.includes(r.status);
  return (
  <button type="button" onClick={(e) => { e.stopPropagation(); onOpen(r); }} data-testid={`week-chip-${r.id}`}
    draggable={movable}
    onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', r.id); onDrag(r); }}
    onDragEnd={() => onDrag(null)}
    className={`block w-full truncate rounded-md border px-1.5 py-1 text-left text-[11px] font-semibold hover:shadow-sm ${movable ? 'cursor-grab active:cursor-grabbing' : ''} ${CHIP[r.status] || CHIP.pending}`}
    title={`${r.ref_no} · ${[r.customer?.prefix, r.customer?.name].filter(Boolean).join(' ')} · ${r.service_type} · ${r.status}${movable ? ' · drag to move' : ''}`}>
    <span className="flex items-center gap-1">
      {movable && <GripVertical size={11} className="hidden shrink-0 opacity-50 lg:block" />}
      {r.pickup?.required && <Truck size={11} className="shrink-0" aria-label="Pickup" />}
      <span className="truncate">{[r.customer?.prefix, r.customer?.name].filter(Boolean).join(' ') || r.ref_no}</span>
    </span>
    <span className="block truncate text-[10px] font-normal opacity-80">{r.service_type}</span>
  </button>
  );
};

const Cell = ({ date, win, rows, cap, blocked, past, onOpen, onNew, dragging, onDrag, onDrop }) => {
  const used = rows.filter((r) => ACTIVE.includes(r.status)).length;
  const full = used >= cap;
  const [over, setOver] = useState(false);
  const droppable = !!dragging && !blocked && !full && !past && !(dragging.slot_date === date && dragging.slot_window === win);
  const tone = blocked || past ? 'bg-slate-100 opacity-70' : full ? 'bg-rose-50' : used >= cap - 1 ? 'bg-amber-50/70' : 'bg-emerald-50/50';
  const canAdd = !blocked && !full && !past;
  return (
    <td className={`h-24 min-w-[150px] border border-slate-200 p-1.5 align-top ${tone} ${canAdd ? 'cursor-pointer hover:ring-2 hover:ring-inset hover:ring-teal-400' : ''}`}
      onClick={() => canAdd && onNew(date, win)} data-testid={`week-cell-${date}-${win}`}
      onDragOver={(e) => { if (droppable) { e.preventDefault(); setOver(true); } }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); if (droppable) onDrop(date, win); }}
      data-droppable={droppable ? 'yes' : 'no'}
      style={over ? { boxShadow: 'inset 0 0 0 2px #0d9488', background: '#ccfbf1' } : droppable ? { boxShadow: 'inset 0 0 0 1px #5eead4' } : undefined}>
      <div className="mb-1 flex items-center justify-between text-[10px] font-bold">
        <span className={blocked ? 'text-slate-400' : full ? 'text-rose-700' : used >= cap - 1 ? 'text-amber-700' : 'text-emerald-700'} data-testid={`week-count-${date}-${win}`}>
          {blocked ? 'Held' : `${used}/${cap} booked`}
        </span>
        {canAdd && <Plus size={12} className="text-teal-700" />}
      </div>
      <div className="space-y-1">{rows.map((r) => <Chip key={r.id} r={r} onOpen={onOpen} onDrag={onDrag} />)}</div>
    </td>
  );
};

export const WeekView = ({ authHeader, meta }) => {
  const navigate = useNavigate();
  const [start, setStartState] = useState(() => mondayOf(sessionStorage.getItem('apt-week') ? `${sessionStorage.getItem('apt-week')}T00:00:00` : new Date()));
  const setStart = (d) => { sessionStorage.setItem('apt-week', iso(d)); setStartState(d); };
  const [rows, setRows] = useState(null);
  const [dragging, setDragging] = useState(null);
  const [move, setMove] = useState(null);
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(start, i)), [start]);

  const load = useCallback(async () => {
    setRows(null);
    try {
      const { data } = await axios.get(`${API}/appointments`, {
        headers: authHeader(), params: { date_from: iso(days[0]), date_to: iso(days[6]), limit: 500 },
      });
      setRows((data.rows || []).filter((r) => !['cancelled', 'no_show'].includes(r.status)));
    } catch (e) { notifyErr(e); setRows([]); }
  }, [authHeader, days]);
  useEffect(() => { load(); }, [load]);

  const cap = meta.max_per_slot || 4;
  const now = new Date();
  const today = iso(now);
  const nowHm = now.toTimeString().slice(0, 5);
  const isPast = (date, w) => date < today || (date === today && w.split('-')[1] <= nowHm);
  // Keep rows for bookings in slots the workshop has since removed.
  const windows = [...new Set([...(meta.slot_windows || []), ...(rows || []).map((r) => r.slot_window).filter(Boolean)])].sort();
  const openDays = meta.open_days || [0, 1, 2, 3, 4, 5];
  const held = meta.held || { 4: meta.friday_blocked_windows || [] };
  const capFor = (w) => (meta.window_caps || {})[w] || cap;
  const byDay = useMemo(() => Object.fromEntries(days.map((d) => [iso(d), summarise((rows || []).filter((r) => r.slot_date === iso(d)))])), [rows, days]);
  const week = summarise(rows || []);
  const openRow = (r) => navigate(`/admin/appointments/${r.id}`);
  const newAt = (date, win) => navigate(`/admin/appointments/new?date=${date}&slot=${encodeURIComponent(win)}`);

  return (
    <div className="rounded-xl border border-slate-200 bg-white" data-testid="week-view">
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 p-3">
        <button type="button" onClick={() => setStart(addDays(start, -7))} className="rounded-lg border border-slate-300 p-1.5 hover:bg-slate-50" aria-label="Previous week" data-testid="week-prev"><ChevronLeft size={16} /></button>
        <button type="button" onClick={() => setStart(mondayOf(new Date()))} className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold hover:bg-slate-50" data-testid="week-today">Today</button>
        <button type="button" onClick={() => setStart(addDays(start, 7))} className="rounded-lg border border-slate-300 p-1.5 hover:bg-slate-50" aria-label="Next week" data-testid="week-next"><ChevronRight size={16} /></button>
        <span className="text-sm font-semibold text-slate-800" data-testid="week-range">
          {days[0].toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} – {days[6].toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
        </span>
        {rows === null && <Loader2 size={15} className="animate-spin text-slate-400" />}
        {rows !== null && (
          <span className="rounded-full bg-slate-100 px-3 py-1 text-[11px] font-semibold text-slate-700" data-testid="week-total">
            Week: {week.count} booking{week.count === 1 ? '' : 's'} · {hrs(week.hours)} · ₹ {Math.round(week.revenue).toLocaleString('en-IN')} est.
          </span>
        )}
        <span className="ml-auto flex items-center gap-3 text-[10px] font-semibold text-slate-500">
          <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded bg-emerald-200" /> Free</span>
          <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded bg-amber-200" /> Filling</span>
          <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded bg-rose-200" /> Full</span>
          <span className="flex items-center gap-1"><Truck size={11} /> Pickup</span>
          {FINE && <span className="hidden items-center gap-1 lg:flex" data-testid="week-drag-hint"><GripVertical size={11} /> Drag a booking to move it</span>}
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-xs" data-testid="week-table">
          <thead>
            <tr>
              <th className="sticky left-0 z-10 w-24 border border-slate-200 bg-slate-50 px-2 py-2 text-left text-[10px] uppercase text-slate-500">Slot</th>
              {days.map((d) => (
                <th key={iso(d)} className={`border border-slate-200 px-2 py-2 text-left ${iso(d) === today ? 'bg-teal-600 text-white' : 'bg-slate-50 text-slate-700'}`} data-testid={`week-day-${iso(d)}`}>
                  <span className="block text-[10px] uppercase">{d.toLocaleDateString('en-IN', { weekday: 'short' })}</span>
                  <span className="text-sm font-bold">{d.getDate()}</span>
                  {rows !== null && (openDays.includes((d.getDay() + 6) % 7) || byDay[iso(d)].count > 0) && (
                    <span className={`mt-1 block text-[10px] font-semibold leading-tight ${iso(d) === today ? 'text-white/90' : 'text-slate-500'}`} data-testid={`week-day-summary-${iso(d)}`}>
                      {byDay[iso(d)].count} bkg · {hrs(byDay[iso(d)].hours)}<br />₹ {Math.round(byDay[iso(d)].revenue).toLocaleString('en-IN')} est.
                    </span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {windows.map((w, wi) => (
              <tr key={w}>
                <th className="sticky left-0 z-10 border border-slate-200 bg-white px-2 py-2 text-left font-mono text-[11px] font-semibold text-slate-700">{w}</th>
                {days.map((d, i) => {
                  const date = iso(d);
                  const closedDay = !openDays.includes(i);
                  if (closedDay && !(rows || []).some((r) => r.slot_date === date)) {
                    return wi === 0 ? <td key={date} rowSpan={windows.length} className="min-w-[90px] border border-slate-200 bg-slate-100 text-center text-[11px] font-semibold text-slate-400" data-testid={`week-closed-${date}`}>Closed</td> : null;
                  }
                  return (
                    <Cell key={date} date={date} win={w} cap={capFor(w)} past={isPast(date, w)}
                      blocked={closedDay || !(meta.slot_windows || []).includes(w) || (held[String(i)] || []).includes(w)}
                      rows={(rows || []).filter((r) => r.slot_date === date && r.slot_window === w)}
                      onOpen={openRow} onNew={newAt}
                      dragging={dragging} onDrag={setDragging} onDrop={(d, w) => { setMove({ row: dragging, date: d, win: w }); setDragging(null); }} />
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {move && <MoveDialog move={move} authHeader={authHeader} onClose={() => setMove(null)} onMoved={load} />}
    </div>
  );
};

const hoursOf = (w) => {
  const [a, b] = (w || '').split('-').map((t) => { const [h, m] = t.split(':').map(Number); return h + (m || 0) / 60; });
  return Number.isFinite(a) && Number.isFinite(b) ? Math.max(0, b - a) : 0;
};
const summarise = (list) => ({
  count: list.length,
  hours: list.reduce((s, r) => s + hoursOf(r.slot_window), 0),
  revenue: list.reduce((s, r) => s + Number(r.packages_total || 0), 0),
});
const hrs = (h) => `${Number.isInteger(h) ? h : h.toFixed(2).replace(/0$/, '')} h`;

const niceDay = (d) => new Date(`${d}T00:00:00`).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });

const MoveDialog = ({ move, authHeader, onClose, onMoved }) => {
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [saved, setSaved] = useState(null);
  const { row, date, win } = move;
  useEffect(() => {
    const esc = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onClose]);
  const go = async () => {
    setSaving(true); setErr('');
    try {
      const { data } = await axios.patch(`${API}/appointments/${row.id}`, { slot_date: date, slot_window: win }, { headers: authHeader() });
      setSaved(data); onMoved();
    } catch (e) { setErr(formatErr(e)); }
    setSaving(false);
  };
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose} data-testid="week-move-dialog">
      <div className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-2 flex items-start justify-between gap-2">
          <p className="text-sm font-semibold text-slate-900">{saved ? 'Booking moved' : `Move ${[row.customer?.prefix, row.customer?.name].filter(Boolean).join(' ') || row.ref_no}?`}</p>
          <button type="button" onClick={onClose} aria-label="Close" data-testid="week-move-close"><X size={16} className="text-slate-500" /></button>
        </div>
        <p className="text-sm text-slate-600" data-testid="week-move-text">
          {niceDay(row.slot_date)} · {row.slot_window} → <b className="text-slate-900">{niceDay(date)} · {win}</b>
        </p>
        {err && <p className="mt-2 text-xs text-rose-700" data-testid="week-move-err">{err}</p>}
        {saved ? (
          <a href={aptWaUrl(saved, 'appointment_reschedule')} target="_blank" rel="noopener noreferrer" onClick={() => setTimeout(onClose, 300)} data-testid="week-move-wa"
            className="mt-4 flex items-center justify-center gap-2 rounded-lg bg-emerald-600 py-2.5 text-sm font-semibold" style={{ color: '#fff' }}>
            <MessageCircle size={15} /> Send new time on WhatsApp
          </a>
        ) : (
          <div className="mt-4 flex gap-2">
            <button type="button" onClick={onClose} className="flex-1 rounded-lg border border-slate-300 py-2 text-sm font-semibold text-slate-700" data-testid="week-move-cancel">Cancel</button>
            <button type="button" onClick={go} disabled={saving} className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-teal-600 py-2 text-sm font-semibold text-white disabled:opacity-50" data-testid="week-move-confirm">
              {saving && <Loader2 size={14} className="animate-spin" />} Move
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
